import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sliders, X } from "lucide-react";
import ProductCard from "../components/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { listProducts, listCategories, fetchSiteContent } from "../lib/api";

const CATEGORIES_STATIC = [
  { id: "all", label: "All Pieces" },
  { id: "shirts", label: "Shirts" },
  { id: "kurtas", label: "Short Kurtas" },
];

const COLORS = [
  { id: "Black", hex: "#1A1A1A" },
  { id: "White", hex: "#b8af92" },
  { id: "Pink", hex: "#d0c8ae" },
  { id: "Green", hex: "#1A1A1A" },
  { id: "Blue", hex: "#1A1A1A" },
  { id: "Beige", hex: "#C9B79C" },
  { id: "Red", hex: "#1A1A1A" },
];

const SORTS = [
  { id: "popularity", label: "Most Loved" },
  { id: "newest", label: "Newest First" },
  { id: "price_asc", label: "Price: Low to High" },
  { id: "price_desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState([0, 10000]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [shopSubheading, setShopSubheading] = useState(
    "Small batch. Hand finished. Shipped in plastic-free packaging from our Ahmedabad atelier."
  );

  useEffect(() => {
    listCategories().then((cats) => setDynamicCategories(cats || [])).catch(() => {});
    fetchSiteContent().then((sc) => {
      if (sc?.shop_subheading) setShopSubheading(sc.shop_subheading);
    }).catch(() => {});
  }, []);

  const CATEGORIES = useMemo(() => {
    const base = [{ id: "all", label: "All Pieces" }];
    return base.concat(dynamicCategories.map((c) => ({ id: c.slug, label: c.display_name || c.name })));
  }, [dynamicCategories]);

  const category = searchParams.get("category") || "all";
  const color = searchParams.get("color") || "";
  const sort = searchParams.get("sort") || "popularity";
  const search = searchParams.get("search") || "";

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (color) params.color = color;
    if (search) params.search = search;
    params.sort = sort;
    params.min_price = price[0];
    params.max_price = price[1];

    listProducts(params)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category, color, sort, search, price]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === "" || value === "all") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const heading = useMemo(() => {
    if (search) return `Results for "${search}"`;
    if (category === "shirts") return "The Shirt Archive";
    if (category === "kurtas") return "The Kurta Collection";
    return "The Complete Edit";
  }, [category, search]);

  const FilterPanel = () => (
    <div className="space-y-10" data-testid="filter-panel">
      {/* Category */}
      <div>
        <p className="eyebrow mb-4">Category</p>
        <ul className="space-y-2">
          {CATEGORIES.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => updateParam("category", c.id)}
                className={`text-sm text-left w-full py-1 transition-colors ${
                  category === c.id
                    ? "text-[#1A1A1A] font-medium"
                    : "text-[#4A4A4A] hover:text-[#1A1A1A]"
                }`}
                data-testid={`filter-category-${c.id}`}
              >
                {c.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Colour */}
      <div>
        <p className="eyebrow mb-4">Colour</p>
        <div className="flex flex-wrap gap-3">
          {COLORS.map((c) => (
            <button
              key={c.id}
              onClick={() => updateParam("color", color === c.id ? "" : c.id)}
              className={`w-9 h-9 rounded-full border-2 transition-all ${
                color === c.id
                  ? "border-[#1A1A1A] scale-110"
                  : "border-[#a89e80] hover:border-[#6b6353]"
              }`}
              style={{ backgroundColor: c.hex }}
              aria-label={c.id}
              title={c.id}
              data-testid={`filter-color-${c.id.toLowerCase()}`}
            />
          ))}
        </div>
      </div>

      {/* Price */}
      <div>
        <p className="eyebrow mb-5">Price</p>
        <Slider
          value={price}
          onValueChange={setPrice}
          min={0}
          max={10000}
          step={500}
          className="mb-4"
          data-testid="filter-price-slider"
        />
        <div className="flex justify-between text-xs text-[#4A4A4A]">
          <span>₹{price[0].toLocaleString("en-IN")}</span>
          <span>₹{price[1].toLocaleString("en-IN")}</span>
        </div>
      </div>

      {/* Popularity checkbox */}
      <div>
        <p className="eyebrow mb-4">Curated</p>
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <Checkbox
            checked={sort === "popularity"}
            onCheckedChange={(v) => updateParam("sort", v ? "popularity" : "")}
            data-testid="filter-most-loved"
          />
          Most Loved
        </label>
        <label className="flex items-center gap-3 text-sm mt-3 cursor-pointer">
          <Checkbox
            checked={sort === "newest"}
            onCheckedChange={(v) => updateParam("sort", v ? "newest" : "")}
            data-testid="filter-newest"
          />
          New Arrivals
        </label>
      </div>

      <Button
        variant="outline"
        className="w-full border-[#1A1A1A] rounded-none text-xs tracking-[0.22em] uppercase py-5"
        onClick={() => setSearchParams({})}
        data-testid="filter-clear-all"
      >
        Clear All
      </Button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="shop-page">
      <div className="mb-12 md:mb-16">
        <p className="eyebrow">The Collection</p>
        <h1 className="font-serif-display text-4xl md:text-6xl mt-3">{heading}</h1>
        <p className="text-[#4A4A4A] mt-3 text-[15px] max-w-xl" data-testid="shop-subheading">
          {shopSubheading}
        </p>
      </div>

      <div className="flex items-center justify-between mb-8 pb-4 border-b border-[#a89e80]">
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="lg:hidden flex items-center gap-2 text-xs tracking-[0.22em] uppercase"
          data-testid="mobile-filters-open"
        >
          <Sliders size={16} /> Filters
        </button>
        <p className="hidden lg:block text-xs tracking-[0.22em] uppercase text-[#4A4A4A]" data-testid="product-count">
          {products.length} pieces
        </p>
        <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
          <SelectTrigger
            className="w-[220px] border-0 border-b border-[#a89e80] rounded-none bg-transparent text-xs tracking-[0.22em] uppercase focus:ring-0 shadow-none"
            data-testid="sort-dropdown"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#c3ba9e]">
            {SORTS.map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`sort-option-${s.id}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12 md:gap-16">
        <aside className="hidden lg:block">
          <FilterPanel />
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-[#b8af92] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center">
              <p className="font-serif-display text-3xl">No pieces match your search.</p>
              <p className="text-sm text-[#7A7A7A] mt-3">Try clearing filters or exploring another category.</p>
              <Button
                variant="outline"
                onClick={() => setSearchParams({})}
                className="mt-6 rounded-none border-[#1A1A1A] tracking-[0.22em] uppercase text-xs"
              >
                Show all
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8" data-testid="product-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filters overlay */}
      {mobileFiltersOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#c3ba9e]" data-testid="mobile-filters-overlay">
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#a89e80]">
            <span className="eyebrow">Filters</span>
            <button onClick={() => setMobileFiltersOpen(false)} aria-label="Close" data-testid="mobile-filters-close">
              <X size={22} />
            </button>
          </div>
          <div className="p-6 overflow-y-auto h-[calc(100vh-70px)]">
            <FilterPanel />
          </div>
        </div>
      )}
    </div>
  );
}
